import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createAPBill, approveAPBill, recordAPBillPayment, batchPayAPBills, listAPPayments, fetchAPBill, listSuppliers, listGLAccounts, listCases, fetchCreditorAging, fetchTrialBalance, fetchAllPages } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-600',
  APPROVED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
  PAID: 'bg-green-100 text-green-700',
  VOIDED: 'bg-red-100 text-red-600',
  REVERSED: 'bg-gray-300 text-gray-700',
  OVERDUE: 'bg-red-100 text-red-600',
};

const VAT_TREATMENT_OPTIONS = [
  { value: 'STANDARD', label: 'Standard (VAT applies)' },
  { value: 'EXEMPT', label: 'VAT Exempt' },
  { value: 'ZERO_RATED', label: 'Zero-Rated' },
];

const EMPTY_BILL_FORM = {
  supplier: '', case: '', date_received: new Date().toISOString().slice(0, 10), due_date: '', bill_number: '', notes: '',
  vat_input_method: 'AMOUNT',
  items: [{ description: '', gl_account: '', quantity: 1, unit_price: '', tax_amount: '0', vat_treatment: 'STANDARD' }],
  receipt_file: null,
};

function CreditorManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cases, setCases] = useState([]);
  const [aging, setAging] = useState(null);
  const [glControlBalance, setGlControlBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('bills'); // bills | aging
  const [showBillForm, setShowBillForm] = useState(false);
  const [showPayForm, setShowPayForm] = useState(null);
  const [viewingBill, setViewingBill] = useState(null);
  const [viewingPayments, setViewingPayments] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('BANK_TRANSFER');
  const [payRef, setPayRef] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_BILL_FORM);
  const [selectedBills, setSelectedBills] = useState([]);
  const [isSubmittingBill, setIsSubmittingBill] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isBatchPaying, setIsBatchPaying] = useState(false);
  const isSubmittingBillRef = useRef(false);
  const isSubmittingPaymentRef = useRef(false);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      // fetchAllPages walks every backend page — a plain single GET here
      // (docs/audits/pagination-audit-2026-08-13.md) silently truncated a
      // firm's AP bill list at 10 rows.
      const data = await fetchAllPages(authAxios, 'ap-bills/', params);
      setBills(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load bills.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter]);

  const fetchRef = useCallback(async () => {
    try {
      const [s, a, c] = await Promise.all([
        listSuppliers(authAxios, { is_active: true }),
        listGLAccounts(authAxios, { is_active: true }),
        listCases(authAxios),
      ]);
      setSuppliers(Array.isArray(s) ? s : []);
      setAccounts(Array.isArray(a) ? a : []);
      setCases(Array.isArray(c) ? c : []);
    } catch {
      showFormMessage('error', 'Failed to load suppliers, GL accounts, or cases.');
    }
  }, [authAxios, showFormMessage]);

  const fetchAgingReport = useCallback(async () => {
    try {
      const data = await fetchCreditorAging(authAxios);
      setAging(data);
    } catch { showFormMessage('error', 'Failed to load aging report.'); }
    try {
      const tb = await fetchTrialBalance(authAxios);
      const row = (tb?.rows || []).find(r => r.code === '2000');
      if (row) setGlControlBalance((parseFloat(row.credit) || 0) - (parseFloat(row.debit) || 0));
      else setGlControlBalance(null);
    } catch {
      setGlControlBalance(null);
    }
  }, [authAxios]);

  useEffect(() => { fetchBills(); fetchRef(); }, [fetchBills, fetchRef]);
  useEffect(() => { if (activeView === 'aging') fetchAgingReport(); }, [activeView, fetchAgingReport]);

  // Auto-open a bill's detail panel when deep-linked from Accounting Review.
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'creditors' || detail.type !== 'ap_bill') return;
      setActiveView('bills');
      openDetailPanel(detail.id);
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [bills]);

  const addItem = () => setFormData(f => ({ ...f, items: [...f.items, { description: '', gl_account: '', quantity: 1, unit_price: '', tax_amount: '0', vat_treatment: 'STANDARD' }] }));
  const removeItem = (idx) => setFormData(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => setFormData(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [field]: value };
    return { ...f, items };
  });

  const billTotal = useMemo(() => formData.items.reduce((s, it) => s + ((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0)) + (parseFloat(it.tax_amount) || 0), 0), [formData.items]);

  const handleSubmitBill = async (e) => {
    e.preventDefault();
    if (isSubmittingBillRef.current) return;
    isSubmittingBillRef.current = true;
    setIsSubmittingBill(true);
    setFormErrors({});
    try {
      const isPercentage = formData.vat_input_method === 'PERCENTAGE';
      const payload = {
        supplier: parseInt(formData.supplier),
        case: formData.case ? parseInt(formData.case) : null,
        date_received: formData.date_received,
        due_date: formData.due_date || null,
        bill_number: formData.bill_number,
        notes: formData.notes,
        vat_input_method: formData.vat_input_method,
        items: formData.items.filter(i => i.description && i.unit_price).map(i => {
          const item = {
            description: i.description,
            gl_account: i.gl_account ? parseInt(i.gl_account) : null,
            quantity: parseInt(i.quantity) || 1,
            unit_price: parseFloat(i.unit_price),
            vat_treatment: i.vat_treatment || 'STANDARD',
          };
          if (isPercentage) {
            item.tax_percentage = parseFloat(i.tax_amount) || 0;
          } else {
            item.tax_amount = parseFloat(i.tax_amount) || 0;
          }
          return item;
        }),
      };
      // If receipt_file present use FormData
      if (formData.receipt_file) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (k === 'items') fd.append('items_input', JSON.stringify(v));
          else if (v !== null && v !== undefined) fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
        });
        fd.append('receipt_file', formData.receipt_file);
        await createAPBill(authAxios, fd);
      } else {
        await createAPBill(authAxios, payload);
      }
      showFormMessage('success', 'Bill created.');
      setShowBillForm(false);
      setFormData(EMPTY_BILL_FORM);
      fetchBills();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.detail) {
        // Field-level errors from API
        setFormErrors(data);
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
        showFormMessage('error', msgs.join(' | '));
      } else {
        showFormMessage('error', data?.detail || 'Failed to create bill.');
      }
    } finally {
      isSubmittingBillRef.current = false;
      setIsSubmittingBill(false);
    }
  };

  const handleApprove = async (id) => {
    const ok = await confirm({ title: 'Approve Bill', message: 'Approve this bill for payment?', confirmText: 'Approve' });
    if (!ok) return;
    try {
      await approveAPBill(authAxios, id);
      showFormMessage('success', 'Bill approved.');
      fetchBills();
      if (viewingBill && viewingBill.id === id) openDetailPanel(id);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to approve.');
    }
  };

  const handlePay = async (billId) => {
    if (isSubmittingPaymentRef.current) return;
    const ok = await confirm({ title: 'Record Payment', message: `Record a payment of ${payAmount ? formatCurrency(payAmount) : 'this amount'}?`, confirmText: 'Record Payment' });
    if (!ok) return;
    isSubmittingPaymentRef.current = true;
    setIsSubmittingPayment(true);
    try {
      await recordAPBillPayment(authAxios, billId, { amount: payAmount, payment_method: payMethod, reference: payRef });
      showFormMessage('success', 'Payment recorded.');
      setShowPayForm(null);
      setPayAmount(''); setPayMethod('BANK_TRANSFER'); setPayRef('');
      fetchBills();
      if (viewingBill && viewingBill.id === billId) openDetailPanel(billId);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to record payment.');
    } finally {
      isSubmittingPaymentRef.current = false;
      setIsSubmittingPayment(false);
    }
  };

  const toggleSelectBill = (id) => setSelectedBills(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleBatchPay = async () => {
    if (selectedBills.length === 0) { showFormMessage('error', 'Select bills to pay.'); return; }
    const ok = await confirm({ title: 'Batch Pay', message: `Pay ${selectedBills.length} selected bill(s) in full?`, confirmText: 'Pay All', variant: 'danger' });
    if (!ok) return;
    setIsBatchPaying(true);
    try {
      await batchPayAPBills(authAxios, { bill_ids: selectedBills, payment_method: 'BANK_TRANSFER' });
      showFormMessage('success', `${selectedBills.length} bill(s) paid.`);
      setSelectedBills([]);
      fetchBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Batch pay failed.');
    } finally {
      setIsBatchPaying(false);
    }
  };

  const openDetailPanel = async (billId) => {
    // Fetch the bill directly rather than reading from the `bills` list state —
    // that state can be stale immediately after a mutation (fetchBills() is
    // async and may not have resolved yet when this is called to refresh
    // an already-open panel).
    try {
      const bill = await fetchAPBill(authAxios, billId);
      setViewingBill(bill);
    } catch {
      showFormMessage('error', 'Failed to load bill details.');
      setViewingBill(b => b && b.id === billId ? b : { id: billId });
    }
    setIsLoadingPayments(true);
    try {
      const payments = await listAPPayments(authAxios, { bill: billId });
      setViewingPayments(Array.isArray(payments) ? payments : []);
    } catch {
      showFormMessage('error', 'Failed to load payment history.');
      setViewingPayments([]);
    } finally {
      setIsLoadingPayments(false);
    }
  };
  const closeDetailPanel = () => { setViewingBill(null); setViewingPayments([]); };

  const filtered = useMemo(() => {
    if (!searchTerm) return bills;
    const term = searchTerm.toLowerCase();
    return bills.filter(b => (b.bill_number || '').toLowerCase().includes(term) || (b.supplier_name || b.supplier?.name || '').toLowerCase().includes(term));
  }, [bills, searchTerm]);

  const BUCKET_LABELS = { current: 'Current', '30_days': '1-30 Days', '60_days': '31-60 Days', '90_days': '61-90 Days', over_90: 'Over 90 Days' };
  const glMismatch = aging && glControlBalance != null && Math.abs(glControlBalance - (aging.grand_total || 0)) > 0.01;

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Creditor Management</h2>
          <p className="text-gray-500 text-sm mt-1">Accounts payable, bills and aging</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveView('bills')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeView === 'bills' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Bills</button>
          <button onClick={() => setActiveView('aging')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeView === 'aging' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Aging Report</button>
          {canManage && activeView === 'bills' && selectedBills.length > 0 && (
            <button onClick={handleBatchPay} disabled={isBatchPaying} aria-label={`Batch pay ${selectedBills.length} selected bills`}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {isBatchPaying ? 'Paying…' : `Batch Pay (${selectedBills.length})`}
            </button>
          )}
          {canManage && (
            <button onClick={() => setShowBillForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">+ New Bill</button>
          )}
        </div>
      </div>

      {/* BILLS VIEW */}
      {activeView === 'bills' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input type="text" placeholder="Search bills..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="APPROVED">Approved</option>
              <option value="PARTIALLY_PAID">Partially Paid</option>
              <option value="PAID">Paid</option>
              <option value="REVERSED">Reversed</option>
              <option value="VOIDED">Voided</option>
            </select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No bills found.</p></div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {canManage && (
                      <th className="px-4 py-3 w-10">
                        <input type="checkbox" aria-label="Select all approved bills"
                          onChange={e => setSelectedBills(e.target.checked ? filtered.filter(b => b.status === 'APPROVED').map(b => b.id) : [])}
                          className="rounded border-gray-300" />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Receipt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(b => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      {canManage && (
                        <td className="px-4 py-3">
                          {b.status === 'APPROVED' && (
                            <input type="checkbox" aria-label={`Select bill ${b.bill_number}`} checked={selectedBills.includes(b.id)} onChange={() => toggleSelectBill(b.id)} className="rounded border-gray-300" />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-blue-600">
                        <button onClick={() => openDetailPanel(b.id)} aria-label={`View bill ${b.bill_number}`} className="hover:underline">{b.bill_number}</button>
                      </td>
                      <td className="px-4 py-3 text-gray-800">{b.supplier_name || b.supplier?.name || ''}</td>
                      <td className="px-4 py-3 text-gray-600">{b.case_title || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{b.date_received}</td>
                      <td className="px-4 py-3 text-gray-600">{b.due_date || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(b.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-right font-medium text-red-600">{formatCurrency(b.balance_due || 0)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                          {(b.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {b.receipt_file ? (
                          <a href={b.receipt_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs font-medium underline">View</a>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button onClick={() => openDetailPanel(b.id)} aria-label={`View details for bill ${b.bill_number}`} className="text-gray-600 hover:text-gray-900 text-xs font-medium">View</button>
                        {canManage && b.status === 'DRAFT' && <button onClick={() => handleApprove(b.id)} aria-label={`Approve bill ${b.bill_number}`} className="text-green-600 hover:text-green-800 text-xs font-medium">Approve</button>}
                        {canManage && ['APPROVED', 'PARTIALLY_PAID'].includes(b.status) && (
                          <button onClick={() => { setShowPayForm(b.id); setPayAmount(''); }} aria-label={`Record payment for bill ${b.bill_number}`} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Pay</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* AGING VIEW */}
      {activeView === 'aging' && (
        aging ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {Object.entries(BUCKET_LABELS).map(([key, label]) => (
                <div key={key} className="bg-white rounded-xl shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{label}</p>
                  <p className="text-lg font-bold text-gray-800 mt-1">{formatCurrency(aging.totals?.[key] || 0)}</p>
                  <p className="text-xs text-gray-400">{(aging.buckets?.[key] || []).length} bills</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(aging.grand_total || 0)}</p>
              </div>
              {aging.upcoming_total != null && (
                <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                  <p className="text-sm text-gray-500">Upcoming (Next 30 Days)</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(aging.upcoming_total)}</p>
                </div>
              )}
              <div className={`rounded-xl shadow-sm p-4 text-center ${glMismatch ? 'bg-red-50' : 'bg-white'}`}>
                <p className="text-sm text-gray-500">GL Control Account (2000)</p>
                {glControlBalance != null ? (
                  <>
                    <p className={`text-2xl font-bold ${glMismatch ? 'text-red-700' : 'text-gray-800'}`}>{formatCurrency(glControlBalance)}</p>
                    {glMismatch && <p className="text-xs text-red-600 mt-1">Does not match subledger total — reconcile.</p>}
                  </>
                ) : <p className="text-sm text-gray-400 mt-2">Not available</p>}
              </div>
            </div>
            {Object.entries(BUCKET_LABELS).map(([key, label]) => {
              const items = aging.buckets?.[key] || [];
              if (items.length === 0) return null;
              return (
                <div key={key}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">{label}</h3>
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Bill #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Supplier</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Due Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Balance</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Days Overdue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono">{item.bill_number}</td>
                            <td className="px-4 py-2">{item.supplier}</td>
                            <td className="px-4 py-2 text-gray-600">{item.due_date || '—'}</td>
                            <td className="px-4 py-2 text-right font-medium text-red-600">{formatCurrency(item.balance)}</td>
                            <td className="px-4 py-2 text-right text-gray-600">{item.days_overdue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        )
      )}

      {/* New Bill Form */}
      {showBillForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New Bill</h3>
            <form onSubmit={handleSubmitBill} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                  <select required value={formData.supplier} onChange={e => setFormData({ ...formData, supplier: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Number *</label>
                  <input type="text" required value={formData.bill_number} onChange={e => setFormData({ ...formData, bill_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. INV-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date</label>
                  <input type="date" value={formData.date_received} onChange={e => setFormData({ ...formData, date_received: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.due_date ? 'border-red-400' : 'border-gray-300'}`} />
                  {formErrors.due_date && <p className="text-red-500 text-xs mt-1">{Array.isArray(formErrors.due_date) ? formErrors.due_date[0] : formErrors.due_date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case / Matter (optional)</label>
                  <select value={formData.case} onChange={e => setFormData({ ...formData, case: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">— No case (firm-level) —</option>
                    {cases.map(c => <option key={c.id} value={c.id}>{`${c.case_number || c.id} — ${c.title}`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Input Method</label>
                  <select value={formData.vat_input_method} onChange={e => setFormData({ ...formData, vat_input_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="AMOUNT">Enter VAT Amount</option>
                    <option value="PERCENTAGE">Enter VAT Percentage</option>
                  </select>
                </div>
              </div>
              {formErrors.items && <p className="text-red-500 text-xs">{Array.isArray(formErrors.items) ? formErrors.items[0] : formErrors.items}</p>}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Line Items</label>
                  <button type="button" onClick={addItem} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Add Item</button>
                </div>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="mb-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                      <select value={item.gl_account} onChange={e => updateItem(idx, 'gl_account', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="">GL Account</option>
                        {accounts.filter(a => a.category === 'EXPENSE').map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                      </select>
                      <input type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        className="col-span-1 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" placeholder="Unit Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" placeholder={formData.vat_input_method === 'PERCENTAGE' ? 'VAT %' : 'VAT Amt'}
                        value={item.tax_amount} onChange={e => updateItem(idx, 'tax_amount', e.target.value)}
                        className="col-span-1 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                        disabled={item.vat_treatment !== 'STANDARD'} />
                      <select value={item.vat_treatment} onChange={e => updateItem(idx, 'vat_treatment', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-xs">
                        {VAT_TREATMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button type="button" onClick={() => removeItem(idx)} aria-label={`Remove line item ${idx + 1}`} className="col-span-1 text-red-400 hover:text-red-600 text-sm">✕</button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-sm font-medium mt-1">Total: {formatCurrency(billTotal)}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt / Invoice File (optional)</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setFormData({ ...formData, receipt_file: e.target.files[0] || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowBillForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" disabled={isSubmittingBill} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingBill ? 'Creating…' : 'Create Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal — z-[60]: can be opened from inside the Bill Detail
          Panel (z-50), so it must stack above it, not just above the page. */}
      {showPayForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP)</label>
                <input type="number" step="0.01" min="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="BANK_TRANSFER">Bank Transfer / EFT</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" value={payRef} onChange={e => setPayRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowPayForm(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={() => handlePay(showPayForm)} disabled={isSubmittingPayment} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingPayment ? 'Recording…' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bill Detail Panel */}
      {viewingBill && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailPanel}></div>
            <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-2xl">
                <div className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-4 py-6 sm:px-6 bg-white border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Bill {viewingBill.bill_number} {viewingBill.internal_ref ? `(${viewingBill.internal_ref})` : ''}</h2>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${STATUS_COLORS[viewingBill.status] || 'bg-gray-50 text-gray-700 ring-gray-200'}`}>
                            {(viewingBill.status || '').replace(/_/g, ' ')}
                          </span>
                          <span>{viewingBill.supplier_name || viewingBill.supplier?.name}</span>
                          <span className="text-gray-300">•</span>
                          <span>Received {viewingBill.date_received}</span>
                          <span className="text-gray-300">•</span>
                          <span>Due {viewingBill.due_date || '—'}</span>
                        </div>
                      </div>
                      <div className="ml-3 h-7 flex items-center">
                        <button onClick={closeDetailPanel} aria-label="Close bill details" className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">×</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 gap-3 mb-4 rounded-lg border border-gray-200 bg-white p-4">
                      {viewingBill.case_title && (
                        <div className="text-sm">
                          <p className="text-gray-600">Case / Matter</p>
                          <p className="font-semibold text-gray-900">{viewingBill.case_title}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Subtotal</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingBill.subtotal || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">VAT</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingBill.vat_amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Total</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingBill.total_amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200">
                          <p className="text-xs text-emerald-800">Balance Due</p>
                          <p className="font-semibold text-emerald-900">{formatCurrency(viewingBill.balance_due || 0)}</p>
                        </div>
                      </div>
                      {(viewingBill.receipt_file || viewingBill.notes) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm pt-2 border-t border-gray-100">
                          {viewingBill.receipt_file && (
                            <div>
                              <p className="text-gray-600">Receipt</p>
                              <a href={viewingBill.receipt_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">View file</a>
                            </div>
                          )}
                          {viewingBill.notes && (
                            <div>
                              <p className="text-gray-600">Notes</p>
                              <p className="text-gray-900">{viewingBill.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {canManage && (
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          {viewingBill.status === 'DRAFT' && (
                            <button onClick={() => handleApprove(viewingBill.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-medium">Approve</button>
                          )}
                          {['APPROVED', 'PARTIALLY_PAID'].includes(viewingBill.status) && (
                            <button onClick={() => { setShowPayForm(viewingBill.id); setPayAmount(''); }} className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium">Record Payment</button>
                          )}
                        </div>
                      )}
                    </div>

                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Line Items</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">GL Account</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">VAT Treatment</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(viewingBill.items || []).map(item => (
                            <tr key={item.id}>
                              <td className="px-3 py-2 text-gray-800">{item.description}</td>
                              <td className="px-3 py-2 text-gray-600">{item.gl_account_name || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{item.vat_treatment}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.amount || 0)}</td>
                            </tr>
                          ))}
                          {(!viewingBill.items || viewingBill.items.length === 0) && (
                            <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">No line items.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment History</h3>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      {isLoadingPayments ? (
                        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>
                      ) : viewingPayments.length === 0 ? (
                        <p className="text-center text-gray-400 py-6 text-sm">No payments recorded yet.</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Method</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Recorded By</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {viewingPayments.map(p => (
                              <tr key={p.id}>
                                <td className="px-3 py-2 text-gray-600">{p.payment_date}</td>
                                <td className="px-3 py-2 text-gray-600">{p.payment_method_display || p.payment_method}</td>
                                <td className="px-3 py-2 text-gray-600">{p.reference || '—'}</td>
                                <td className="px-3 py-2 text-gray-600">{p.created_by_username || '—'}</td>
                                <td className="px-3 py-2 text-right font-medium text-emerald-700">{formatCurrency(p.amount || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreditorManagement;
